import { graphql, useStaticQuery } from "gatsby";
import React, { useRef } from "react";
import SwiperCore from "swiper";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { useFilteredGeographical } from "hooks/useFilteredGeographical";
import { uniqBy } from "utils/commonFunctions";
import { Link } from "utils/customLink";

import RelatedBg from "../../images/relatedBg.svg";
import { preFilterStandardPrograms } from "../../utils/base";
import CardWithBgImage from "../cards/cardWithBgImage";

import "swiper/css/bundle";
import "swiper/css/navigation";

SwiperCore.use([Autoplay, Pagination, Navigation]);

const RelatedPrograms = ({
  relatedGeoSlugs,
  groupPrograms = false,
  excludeSlug = "",
  innerContainerClass = "sm:bg-white sm:planet:bg-gray-darker",
  isFlexProgram = false,
  program = ""
}) => {
  const swiperRef = useRef(null);

  const listGeographicals = useFilteredGeographical({
    geographicals: relatedGeoSlugs
  });

  const relatedReformattedGroupPrograms =
    groupPrograms === true
      ? uniqBy(
          listGeographicals
            ?.flatMap((geo) => geo?.groupPrograms?.nodes)
            ?.map((program) => ({
              ...program,
              uri: program?.uri.replace(
                "group-travel-single",
                "group-travel/schools"
              )
            }))
            .filter((item) => item),
          "uri"
        )
      : [];

  let relatedStandardPrograms =
    uniqBy(
      listGeographicals
        ?.flatMap((geo) => geo?.programs?.nodes)
        .filter((item) => item),
      "uri"
    ) ?? [];

  const stdPrograms = useStaticQuery(graphql`
    query {
      stdPrograms: allWpProgram(
        sort: { acfProgramsMeta: { programRating: DESC } }
      ) {
        nodes {
          ...programMiniDataFilterableFragment
        }
      }
    }
  `);

  const preselectedProgramFocus =
    program?.acfProgramsMeta?.programFocuses?.flatMap(
      (focus) => focus?.programFocus?.focusPageLink?.slug
    );

  if (isFlexProgram) {
    relatedStandardPrograms =
      stdPrograms?.stdPrograms?.nodes?.filter((program) =>
        preFilterStandardPrograms(
          program,
          [],
          [],
          [],
          preselectedProgramFocus,
          true
        )
      ) ?? [];
  }

  let listPrograms = [
    ...relatedReformattedGroupPrograms,
    ...relatedStandardPrograms
  ];

  if (excludeSlug) {
    listPrograms = listPrograms?.filter(
      (item) => !item?.uri?.includes(excludeSlug)
    );
  }

  if (listPrograms && listPrograms.length <= 5) {
    listPrograms = Array(6).fill(listPrograms).flat();
  }

  const slidePerViewDefault = listPrograms?.length === 1 ? 1 : 1.5;
  const slidePerView = listPrograms?.length < 5 ? listPrograms?.length : 5;

  return (
    <>
      {listPrograms && listPrograms.length > 0 && (
        <section className="bg-gray-lighten planet:bg-gray-dark flex flex-col items-center justify-center mx-auto relative z-10 py-6 min-h-[550px]">
          <div
            className={`w-full z-0 top-0 bottom-0 absolute right-0 left-0 bg-cover bg-no-repeat bg-center opacity-80 planet:opacity-40`}
            style={{
              backgroundImage: `url('${RelatedBg}')`
            }}
          />

          <div className="w-11/12 relative sm:w-full sm:h-full sm:absolute m-0 flex flex-col items-center">
            <div className="relative sm:absolute sm:-translate-y-1/2 flex flex-col sm:w-8/12">
              <div
                className={`flex flex-col w-full justify-center sm:mx-auto pt-14 pb-8 sm:pr-0 sm:py-2 sm:h-24 ${innerContainerClass} `}
              >
                <h1
                  className="font-light text-left text-title font-normal sm:text-center sm:text-[38px] leading-8"
                  dangerouslySetInnerHTML={{
                    __html: "You might also be interested in these programs"
                  }}
                />
              </div>
            </div>

            <div className="flex-col justify-center sm:flex sm:h-full sm:py-16">
              <div className="flex flex-col w-full sm:w-11/12 container mx-auto sm:py-0">
                <div
                  className={`related-programs-swiper-container w-[calc(100vw-32px)] sm:mx-auto h-80 sm:w-11/12`}
                >
                  <Swiper
                    ref={swiperRef}
                    grabCursor={true}
                    centeredSlides={false}
                    slidesPerView={slidePerViewDefault}
                    spaceBetween={15}
                    loop={true}
                    className="related-programs-swiper justify-center items-center pb-14 sm:!py-2"
                    breakpoints={{
                      640: {
                        slidesPerView: slidePerView,
                        spaceBetween: 0,
                        navigation: true,
                        centeredSlides: true
                      }
                    }}
                  >
                    {listPrograms?.map((relatedProg, index) => {
                      const title = relatedProg.title;
                      const fallbackImage =
                        relatedProg.featuredImage?.node?.sourceUrl;
                      const scaledImage =
                        relatedProg.featuredImage?.node?.gatsbyImage;
                      const featuredImage = relatedProg.featuredImage?.node
                        ?.localFile?.extension
                        ? relatedProg.featuredImage?.node?.localFile
                        : fallbackImage;
                      const programUri = relatedProg.uri;
                      const programType = relatedProg.acfProgramsMeta;
                      const type = programType?.programTypes.map(
                        (type) => type.title
                      );

                      if (!featuredImage && !fallbackImage) {
                        return <></>;
                      }

                      return (
                        <SwiperSlide key={index}>
                          <Link to={programUri}>
                            <CardWithBgImage
                              scaledImage={scaledImage}
                              featuredImage={featuredImage}
                              title={title}
                              programType={type}
                              featuredImageFallback={fallbackImage}
                            />
                          </Link>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>

                  {listPrograms?.length >= 2 && (
                    <>
                      <div
                        className="swiper-button-prev rotate-180 !w-11 h-11 border-2 border-black-lighter planet:sm:border-white planet:bg-white/20 after:hidden !hidden lg:!block"
                        onClick={() => swiperRef.current.swiper.slidePrev()}
                      />
                      <div
                        className="swiper-button-next !w-11 h-11 border-2 border-black-lighter planet:sm:border-white planet:bg-white/20 after:hidden !hidden lg:!block"
                        onClick={() => swiperRef.current.swiper.slideNext()}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default RelatedPrograms;
